import * as React from 'react';
import { twMerge } from 'tailwind-merge';

/* ─── Internal: corner tick mark ─── */

type TickPos = 'tl' | 'tr' | 'bl' | 'br';

function Tick({ pos, inset = 0 }: { pos: TickPos; inset?: number }) {
  const size = 10;
  const base: React.CSSProperties = {
    position: 'absolute',
    width: size,
    height: size,
    borderColor: 'var(--color-stroke-strong)',
    borderStyle: 'solid',
    pointerEvents: 'none',
  };

  const posStyles: Record<TickPos, React.CSSProperties> = {
    tl: { top: inset, left: inset, borderWidth: '1px 0 0 1px' },
    tr: { top: inset, right: inset, borderWidth: '1px 1px 0 0' },
    bl: { bottom: inset, left: inset, borderWidth: '0 0 1px 1px' },
    br: { bottom: inset, right: inset, borderWidth: '0 1px 1px 0' },
  };

  return <span style={{ ...base, ...posStyles[pos] }} aria-hidden />;
}

/* ─── Container ─── */

interface EmptyStateProps {
  className?: string;
  children?: React.ReactNode;
}

function EmptyStateRoot({ className, children }: EmptyStateProps) {
  return (
    <div
      className={twMerge(
        'bg-bg relative flex flex-col items-center justify-center gap-5 px-8 py-16 text-center',
        className,
      )}
    >
      <Tick pos="tl" />
      <Tick pos="tr" />
      <Tick pos="bl" />
      <Tick pos="br" />
      {children}
    </div>
  );
}

/* ─── Mark (concentric-circle SVG + logomark) ─── */

function Mark({ size = 96 }: { size?: number }) {
  const r1 = size / 2;
  const r2 = size * 0.3;

  return (
    <div className="relative flex items-center justify-center" style={{ width: size, height: size }}>
      <svg className="absolute inset-0" width={size} height={size} viewBox={`0 0 ${size} ${size}`} fill="none">
        <circle cx={r1} cy={r1} r={r1 - 1} stroke="var(--color-stroke-2)" strokeWidth="1" strokeDasharray="3 3" />
        <circle cx={r1} cy={r1} r={r2} stroke="var(--color-stroke-2)" strokeWidth="1" strokeDasharray="3 3" />
      </svg>
    </div>
  );
}

/* ─── Label (monospace eyebrow) ─── */

function Label({ children, className }: { children?: React.ReactNode; className?: string }) {
  return (
    <span
      className={twMerge('text-primary-9 font-mono text-[10.5px] font-medium tracking-[.12em] uppercase', className)}
    >
      {children}
    </span>
  );
}

/* ─── Title ─── */

function Title({ children, className }: { children?: React.ReactNode; className?: string }) {
  return (
    <h3 className={twMerge('text-text-1 font-mono text-sm font-semibold tracking-wide uppercase', className)}>
      {children}
    </h3>
  );
}

/* ─── Description ─── */

function Description({ children, className }: { children?: React.ReactNode; className?: string }) {
  return <p className={twMerge('text-text-3 max-w-md text-xs leading-relaxed', className)}>{children}</p>;
}

/* ─── Actions ─── */

function Actions({ children, className }: { children?: React.ReactNode; className?: string }) {
  return <div className={twMerge('flex flex-wrap items-center justify-center gap-2', className)}>{children}</div>;
}

/* ─── Stats row ─── */

function Stats({ children, className }: { children?: React.ReactNode; className?: string }) {
  return (
    <div
      className={twMerge(
        'border-stroke-2 mt-2 grid w-full auto-cols-fr grid-flow-col border-t border-dashed pt-4',
        className,
      )}
    >
      {children}
    </div>
  );
}

/* ─── Individual stat ─── */

interface StatProps {
  label: string;
  value: string;
  mono?: boolean;
  ok?: boolean;
  className?: string;
}

function Stat({ label, value, mono, ok, className }: StatProps) {
  return (
    <div className={twMerge('flex flex-col items-center gap-1 px-4', className)}>
      <span className="text-text-3 font-mono text-[10px] tracking-[.1em] uppercase">{label}</span>
      <span className={twMerge('text-text-1 flex items-center gap-1.5 text-xs', mono && 'font-mono')}>
        {ok && <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-400" aria-label="OK" />}
        {value}
      </span>
    </div>
  );
}

/* ─── Compose compound component ─── */

export const EmptyState = Object.assign(EmptyStateRoot, {
  Mark,
  Label,
  Title,
  Description,
  Actions,
  Stats,
  Stat,
});
